import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Delete, Edit, Save, Cancel } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';
import {
  useGetCapabilityQuery,
  useUpdateCapabilityMutation,
  useDeleteCapabilityMutation,
} from '../store/hooks';

export default function CapabilityDetailPage() {
  const navigate = useNavigate();
  const { capabilityId } = useParams<{ capabilityId: string }>();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });

  const {
    data: capability,
    isLoading,
    error,
    refetch,
  } = useGetCapabilityQuery({ capabilityId: capabilityId! }, { skip: !capabilityId });

  const [updateCapability, { isLoading: isUpdating }] = useUpdateCapabilityMutation();
  const [deleteCapability, { isLoading: isDeleting }] = useDeleteCapabilityMutation();

  const handleEditStart = useCallback(() => {
    if (capability) {
      setEditForm({
        name: capability.name,
        description: capability.description || '',
      });
      setIsEditing(true);
    }
  }, [capability]);

  const handleEditCancel = useCallback(() => {
    setIsEditing(false);
    setEditForm({ name: '', description: '' });
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!capabilityId || !editForm.name.trim()) return;

    try {
      await updateCapability({
        capabilityId,
        updateCapabilityRequest: {
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
        },
      }).unwrap();
      setIsEditing(false);
      refetch();
    } catch (err) {
      console.error('Failed to update capability:', err);
    }
  }, [capabilityId, editForm, updateCapability, refetch]);

  const handleDeleteOpen = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!capabilityId) return;

    try {
      await deleteCapability({ capabilityId }).unwrap();
      navigate('/admin-v2/capabilities');
    } catch (err) {
      console.error('Failed to delete capability:', err);
    }
  }, [capabilityId, deleteCapability, navigate]);

  if (!capabilityId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Invalid Capability ID</h2>
            <Button onClick={() => navigate('/admin-v2/capabilities')} className="mt-4">
              Back to Capabilities
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading capability..." />
        </main>
      </div>
    );
  }

  if (error || !capability) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading capability</h2>
            <p className="text-gray-600 mt-2">Unable to fetch capability details.</p>
            <Button onClick={() => navigate('/admin-v2/capabilities')} className="mt-4">
              Back to Capabilities
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2/capabilities" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Capability' : 'Capability Details'}
          </h1>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={handleEditCancel}
                  disabled={isUpdating}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleEditSave}
                  disabled={!editForm.name.trim() || isUpdating}
                >
                  {isUpdating ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outlined" startIcon={<Edit />} onClick={handleEditStart}>
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDeleteOpen}
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <Card>
          <CardContent>
            {isEditing ? (
              <div className="space-y-4">
                <TextField
                  label="Name"
                  fullWidth
                  value={editForm.name}
                  onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  helperText="Unique identifier for this capability"
                />
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  value={editForm.description}
                  onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  helperText="Optional description of what this capability enables"
                />
              </div>
            ) : (
              <div className="space-y-6">
                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Name
                  </Typography>
                  <Typography variant="h6">{capability.name}</Typography>
                </div>

                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {capability.description || (
                      <span className="text-gray-400 italic">No description</span>
                    )}
                  </Typography>
                </div>

                <div>
                  <Typography variant="subtitle2" className="text-gray-500">
                    Capability ID
                  </Typography>
                  <Typography variant="body2" className="font-mono text-gray-600">
                    {capability.capability_id}
                  </Typography>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(new Date(capability.created_at))}
                    </Typography>
                    <Typography variant="body2" className="text-gray-500">
                      {formatRelativeTime(new Date(capability.created_at))}
                    </Typography>
                  </div>

                  <div>
                    <Typography variant="subtitle2" className="text-gray-500">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(new Date(capability.updated_at))}
                    </Typography>
                    <Typography variant="body2" className="text-gray-500">
                      {formatRelativeTime(new Date(capability.updated_at))}
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Capability</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the capability "{capability.name}"? This action cannot
            be undone and may affect bundles that include this capability.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
